export const ROOM_ACCESS_STORAGE_KEY = "room-access";

const toBlockerList = (blockers) => {
  if (!blockers) return [];
  if (Array.isArray(blockers)) {
    return blockers.map((blocker) => String(blocker || "").trim()).filter(Boolean);
  }
  return [String(blockers).trim()].filter(Boolean);
};

const toBlockerCodeList = (codes) => {
  if (!codes) return [];
  if (Array.isArray(codes)) {
    return codes.map((code) => String(code || "").trim().toUpperCase()).filter(Boolean);
  }
  return [String(codes).trim().toUpperCase()].filter(Boolean);
};

export const normalizeRoomAccess = (roomAccess) => {
  const source = roomAccess && typeof roomAccess === "object" ? roomAccess : {};
  const accessEstablishedVia = String(
    source.access_established_via || source.accessEstablishedVia || ""
  ).trim().toUpperCase();

  return {
    ...source,
    enabled: source.enabled === true,
    can_use_digital_key: source.can_use_digital_key === true || source.canUseDigitalKey === true,
    can_show_pin: source.can_show_pin === true || source.canShowPin === true,
    guest_pin_visible: source.guest_pin_visible === true || source.guestPinVisible === true,
    payment_required: source.payment_required === true || source.paymentRequired === true,
    payment_blocks_access: source.payment_blocks_access === true || source.paymentBlocksAccess === true,
    payment_status_verified: source.payment_status_verified === true || source.paymentStatusVerified === true,
    checkin_complete: source.checkin_complete === true || source.checkinComplete === true,
    has_started: source.has_started === true || source.hasStarted === true,
    is_expired: source.is_expired === true || source.isExpired === true,
    access_established:
      source.access_established === true ||
      source.accessEstablished === true ||
      ["DOOR_OPENED", "PMS_CHECKED_IN"].includes(accessEstablishedVia),
    access_established_via: accessEstablishedVia,
    blocker_codes: toBlockerCodeList(source.blocker_codes || source.blockerCodes),
    blockers: toBlockerList(source.blockers),
  };
};

export const getStoredRoomAccess = () => {
  try {
    return normalizeRoomAccess(JSON.parse(localStorage.getItem(ROOM_ACCESS_STORAGE_KEY) || "{}"));
  } catch {
    return normalizeRoomAccess(null);
  }
};

export const storeRoomAccess = (roomAccess) => {
  const normalized = normalizeRoomAccess(roomAccess);
  localStorage.setItem(ROOM_ACCESS_STORAGE_KEY, JSON.stringify(normalized));
  return normalized;
};

export const canUseRoomAccess = (roomAccess) =>
  normalizeRoomAccess(roomAccess).enabled === true;

export const canUseDigitalKey = (roomAccess) => {
  const normalized = normalizeRoomAccess(roomAccess);
  return normalized.enabled === true && normalized.can_use_digital_key === true;
};

export const isRoomAccessTestOverrideEnabled = (roomAccess) => {
  const normalized = normalizeRoomAccess(roomAccess);
  const testOverride = normalized.test_override || normalized.testOverride || {};
  return testOverride.enabled === true;
};

export const canShowRoomPin = (roomAccess) => {
  const normalized = normalizeRoomAccess(roomAccess);
  return normalized.enabled === true && normalized.can_show_pin === true;
};

export const isRoomAccessPaymentRequired = (roomAccess) =>
  normalizeRoomAccess(roomAccess).payment_required === true;

export const isRoomAccessPaymentBlocking = (roomAccess) => {
  const normalized = normalizeRoomAccess(roomAccess);
  return normalized.payment_blocks_access === true || normalized.blocker_codes.includes("PAYMENT_REQUIRED");
};

export const hasNonBlockingRoomAccessPayment = (roomAccess) => {
  const normalized = normalizeRoomAccess(roomAccess);
  return normalized.payment_required === true && !isRoomAccessPaymentBlocking(normalized);
};

export const isRoomAccessEstablished = (roomAccess) =>
  normalizeRoomAccess(roomAccess).access_established === true;

export const getRoomAccessMessage = (roomAccess, fallback = "Room access is currently unavailable.") => {
  const normalized = normalizeRoomAccess(roomAccess);
  if (typeof normalized.message === "string" && normalized.message.trim()) {
    return normalized.message.trim();
  }
  if (normalized.blockers.length > 0) {
    return normalized.blockers.join(" ");
  }
  return fallback;
};

export const redactRoomPinsUnlessAllowed = (rooms) => {
  if (!Array.isArray(rooms)) return [];

  return rooms.map((room) => ({
    ...room,
    access_pin: "",
    pin: "",
  }));
};
